import { useMemo } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import {
  getQueryAutomationQueryKey,
  queryAutomation,
} from '@/generated/hooks/grimoire/v1/api';
import { QueryAutomationStage } from '@/generated/hooks/grimoire/models';
import {
  getCollection,
  getGetCollectionQueryKey,
} from '@/generated/hooks/bishop/collections/v1/api';
import { GetCollectionStage } from '@/generated/hooks/bishop/models';
import type { IPublishCollectionItem } from '../types';

interface IUseAutomationDraftCollectionsResult {
  /** Referenced collections whose draft schema is not yet published. */
  collectionsToPublish: IPublishCollectionItem[];
  isLoading: boolean;
  isError: boolean;
}

/**
 * Resolves the collections a draft automation references and narrows them to the
 * ones whose draft schema has not been published yet, so the publish flow can
 * publish them alongside the automation.
 *
 * Reads the draft automation for its referenced collection ids, then fetches
 * each collection twice: at `stage=DRAFT` for metadata and the latest schema
 * version, and at `stage=PUBLISHED` for the version the published stage is
 * pinned to. A published version of 0 means "never published", whether the stage
 * 404s or resolves to an empty slot. A collection is only
 * treated as unresolvable (an error) when neither stage loads.
 */
export function useAutomationDraftCollections(
  organization: string,
  workspace: string,
  draftId: string,
  enabled: boolean,
): IUseAutomationDraftCollectionsResult {
  const canQuery = enabled && !!organization && !!workspace && !!draftId;

  const {
    data: automationData,
    isLoading: isAutomationLoading,
    isError: isAutomationError,
  } = useQuery({
    queryKey: getQueryAutomationQueryKey(organization, workspace, draftId, {
      stage: QueryAutomationStage.AUTOMATION_STAGE_DRAFT,
    }),
    queryFn: ({ signal }) =>
      queryAutomation(
        organization,
        workspace,
        draftId,
        { stage: QueryAutomationStage.AUTOMATION_STAGE_DRAFT },
        { signal },
      ),
    enabled: canQuery,
    staleTime: 60000,
  });

  const collectionIds = useMemo(() => {
    const collections = automationData?.data?.collections ?? {};
    return Array.from(
      new Set(
        Object.values(collections)
          .map(ref => ref.collection_id)
          .filter((id): id is string => !!id),
      ),
    );
  }, [automationData]);

  const collectionQueryConfigs = (stage: GetCollectionStage) =>
    collectionIds.map(id => ({
      queryKey: getGetCollectionQueryKey(organization, workspace, id, {
        stage,
      }),
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        getCollection(organization, workspace, id, { stage }, { signal }),
      // Publish status changes as drafts are edited/published — always refetch.
      staleTime: 0,
      // A 404 is an expected "this stage doesn't exist" signal, not a failure to
      // retry; retrying just adds latency before the dialog can render.
      retry: false,
      enabled: canQuery && !!id,
    }));

  const draftStageQueries = useQueries({
    queries: collectionQueryConfigs(GetCollectionStage.COLLECTION_STAGE_DRAFT),
  });
  const publishedStageQueries = useQueries({
    queries: collectionQueryConfigs(
      GetCollectionStage.COLLECTION_STAGE_PUBLISHED,
    ),
  });

  const collectionsToPublish = useMemo(() => {
    const items: IPublishCollectionItem[] = [];
    collectionIds.forEach((id, i) => {
      const draft = draftStageQueries[i]?.data?.data;
      const published = publishedStageQueries[i]?.data?.data;
      if (!draft) return;
      const publishedVersion = published?.schema_version ?? 0;
      // An edit after the first publish leaves a newer draft schema to promote.
      const isUpToDate =
        publishedVersion > 0 && (draft.schema_version ?? 0) <= publishedVersion;
      if (isUpToDate) return;
      items.push({
        id,
        name:
          draft.name ??
          `organizations/${organization}/workspaces/${workspace}/collections/${id}`,
        displayName: draft.display_name || id,
      });
    });
    return items;
  }, [
    collectionIds,
    draftStageQueries,
    publishedStageQueries,
    organization,
    workspace,
  ]);

  const isLoading =
    (canQuery && isAutomationLoading) ||
    draftStageQueries.some(query => query.isLoading) ||
    publishedStageQueries.some(query => query.isLoading);
  // A referenced collection is only unresolvable when neither stage loads; a
  // 404 on one stage is expected (the collection lives only in the other).
  const isError =
    isAutomationError ||
    collectionIds.some(
      (_, i) =>
        draftStageQueries[i]?.isError && publishedStageQueries[i]?.isError,
    );

  return { collectionsToPublish, isLoading, isError };
}

export default useAutomationDraftCollections;
